import * as Sentry from "@sentry/react";

/**
 * Sentry integration. No-op when VITE_SENTRY_DSN is not set, so the app keeps
 * working unchanged in any environment without telemetry configured.
 *
 * By default only initialises in production. Set VITE_SENTRY_ENABLE_DEV=true
 * to test locally without burning quota.
 */

let initialised = false;

export function initSentry(): void {
  if (initialised) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const enableDev = import.meta.env.VITE_SENTRY_ENABLE_DEV === "true";

  if (!dsn) {
    console.info("[sentry] disabled, no DSN set");
    return;
  }

  if (!import.meta.env.PROD && !enableDev) {
    console.info("[sentry] disabled in dev (set VITE_SENTRY_ENABLE_DEV=true to enable)");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION as string | undefined,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event, hint) {
      const err = hint?.originalException;
      const message =
        (err instanceof Error ? err.message : typeof err === "string" ? err : event.message) ?? "";
      const name = err instanceof Error ? err.name : "";

      if (name === "AbortError") return null;
      if (/ResizeObserver loop/i.test(message)) return null;

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        if (/Failed to fetch|NetworkError|Load failed/i.test(message)) return null;
      }

      return event;
    },
  });

  initialised = true;
  console.info("[sentry] initialised", { env: import.meta.env.MODE });
}

export function setSentryUser(user: { id: string; email?: string } | null): void {
  if (!initialised) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

export function addBreadcrumb(crumb: {
  category: "navigation" | "edge-function" | "auth";
  message: string;
  level?: "info" | "warning" | "error";
  data?: Record<string, unknown>;
}): void {
  if (!initialised) return;
  Sentry.addBreadcrumb({
    category: crumb.category,
    message: crumb.message,
    level: crumb.level ?? "info",
    data: crumb.data,
  });
}

export function captureHandledError(
  err: Error,
  ctx: { source: "promise" | "runtime" | "render"; functionName: string | null },
): void {
  if (!initialised) return;
  Sentry.captureException(err, {
    tags: {
      source: ctx.source,
      ...(ctx.functionName ? { functionName: ctx.functionName } : {}),
    },
  });
}
